import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import {
  eatDayOf,
  formatEatTime,
  formatIsoDay,
  REFERRAL_CHANNEL_LABELS,
  REFERRAL_CHANNELS,
  REFERRAL_EVENT_KIND_LABELS,
  REFERRAL_ORIGIN_LABELS,
  REFERRAL_URGENCY_LABELS,
  type ReferralChannel,
  type ReferralUpdateKind,
} from '@wodi/domain';
import type { ReferralDetailView, ReferralFeedbackLetterView } from '@wodi/shared';
import {
  Button,
  DateField,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  RadioGroup,
  Section,
  Select,
  Tag,
  TextArea,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  feedbackEnteredInError,
  recordReferralUpdate,
  referralEnteredInError,
  referralKeys,
  referralQuery,
} from '../api/referrals.ts';
import { can, sessionQuery } from '../api/session.ts';
import { ReferralPrinter } from '../documents/prints.tsx';
import { useOnline } from '../hooks/use-online.ts';
import { eatInstant } from '../imaging/format.ts';
import { outsideDialogs, ShortcutHint } from '../layout/screen.tsx';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { formatInstant, formatIsoDate } from '../patients/format.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import { FeedbackPrinter, WriteFeedbackLetter } from './feedback.tsx';
import { referralStatusTag } from './labels.ts';
import styles from './referrals.module.css';

// One referral (docs/plans/phase2-referrals.md §1.1 item 7, §6): the patient banner, what was
// referred, the letter or the feedback letters, "Record update" with the steps that fit now,
// and the history, oldest first. The national referral exchange state is always in words.

const route = getRouteApi('/_app/referrals/$referralId');

/** What "Record update" offers, in the words a person uses. */
const UPDATE_WORDS: Record<ReferralUpdateKind, string> = {
  acknowledged: 'Acknowledged by the receiving facility',
  attended: 'Patient attended',
  feedback_received: 'Feedback received',
  closed: 'Close the referral',
  note: 'Note',
};

const NOT_SAID = 'not_said';

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function RecordUpdateDialog({
  referral,
  onClose,
}: {
  referral: ReferralDetailView;
  onClose: () => void;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<ReferralUpdateKind | null>(referral.allowedUpdates[0] ?? null);
  const now = new Date();
  // The form opens on "now" to the minute. Left as it is, no time is sent and the server
  // records the exact moment, so an update just after the referral is never "before" it.
  const [opened] = useState(() => ({ day: formatIsoDay(eatDayOf(now)), time: formatEatTime(now) }));
  const [day, setDay] = useState<string | null>(opened.day);
  const [time, setTime] = useState(opened.time);
  const [channel, setChannel] = useState<string>(NOT_SAID);
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const at = day === null ? null : eatInstant(day, time);
  const attended = referral.events.some((e) => e.kind === 'attended');
  const noteNeeded =
    kind === 'note' ||
    kind === 'feedback_received' ||
    (kind === 'closed' && referral.direction === 'outgoing' && !attended);
  const noteMissing = noteNeeded && note.trim() === '';
  const save = useMutation({
    mutationFn: () =>
      recordReferralUpdate(referral.id, {
        kind: kind ?? 'note',
        ...(day === opened.day && time.trim() === opened.time
          ? {}
          : { occurredAt: at?.toISOString() }),
        channel: channel === NOT_SAID ? null : (channel as ReferralChannel),
        note: note.trim() === '' ? null : note.trim(),
      }),
    onSuccess: async (next) => {
      queryClient.setQueryData(referralKeys.detail(referral.id), next);
      await queryClient.invalidateQueries({ queryKey: referralKeys.all });
      toasts.show({ title: 'Update recorded' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Record update"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <RadioGroup
          label="What happened"
          value={kind ?? ''}
          onChange={(v) => {
            setKind(v as ReferralUpdateKind);
          }}
          options={referral.allowedUpdates.map((k) => ({ id: k, label: UPDATE_WORDS[k] }))}
          {...(tried && kind === null ? { errorMessage: 'Choose what happened.' } : {})}
        />
        <div className={styles.row}>
          <DateField
            label="Day it happened"
            value={day}
            onChange={setDay}
            maxValue={formatIsoDay(eatDayOf(now))}
            isRequired
            {...(tried && day === null ? { errorMessage: 'Choose the day.' } : {})}
          />
          <TextField
            label="Time"
            description="24-hour clock, for example 14:05."
            width="sm"
            value={time}
            onChange={setTime}
            isRequired
            {...(tried && day !== null && at === null
              ? { errorMessage: 'Type the time as HH:MM, like 14:05.' }
              : {})}
          />
        </div>
        <Select
          label="How you heard"
          options={[
            { id: NOT_SAID, label: 'Not said' },
            ...REFERRAL_CHANNELS.map((c) => ({ id: c, label: REFERRAL_CHANNEL_LABELS[c] })),
          ]}
          value={channel}
          onChange={(v) => {
            setChannel(String(v));
          }}
        />
        <TextArea
          label={noteNeeded ? 'Note' : 'Note (optional)'}
          {...(kind === 'closed' && noteNeeded
            ? {
                description:
                  'The patient was never recorded as attending. Say why the referral is being closed.',
              }
            : kind === 'feedback_received'
              ? { description: 'What the receiving facility said.' }
              : {})}
          value={note}
          onChange={setNote}
          rows={3}
          isRequired={noteNeeded}
          {...(tried && noteMissing ? { errorMessage: 'Write the note.' } : {})}
        />
        {save.isError ? (
          <Notice tone="error">{errorText(save.error, "The update couldn't be saved.")}</Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!online}
          isPending={save.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            setTried(true);
            if (kind === null || at === null || noteMissing) return;
            save.mutate();
          }}
        >
          Record update
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Facts({ referral }: { referral: ReferralDetailView }) {
  const r = referral;
  const outgoing = r.direction === 'outgoing';
  return (
    <dl className={styles.facts}>
      <dt>{outgoing ? 'To' : 'From'}</dt>
      <dd>
        {r.otherFacility.name}
        {r.otherFacility.mflCode === null ? null : (
          <>
            {', MFL '}
            <span className="wodi-id">{r.otherFacility.mflCode}</span>
          </>
        )}
        <span className={styles.meta}>
          {r.otherFacility.fromRegistry ? ' From the national registry' : ' Typed; not checked'}
        </span>
      </dd>
      {r.otherService === null ? null : (
        <>
          <dt>{outgoing ? 'Department or clinician' : 'Referring clinician'}</dt>
          <dd>{r.otherService}</dd>
        </>
      )}
      <dt>Urgency</dt>
      <dd>{REFERRAL_URGENCY_LABELS[r.urgency]}</dd>
      <dt>{outgoing ? 'Referred' : 'Received'}</dt>
      <dd>{`${formatInstant(r.referredAt)}, ${REFERRAL_ORIGIN_LABELS[r.origin].toLowerCase()}`}</dd>
      <dt>Visit</dt>
      <dd>
        <span className="wodi-id">{r.visitNumber}</span>
      </dd>
      {outgoing ? null : (
        <>
          <dt>Their reference</dt>
          <dd>{r.number === null ? 'None given' : <span className="wodi-id">{r.number}</span>}</dd>
          <dt>Their letter dated</dt>
          <dd>{r.theirLetterDate === null ? 'Not given' : formatIsoDate(r.theirLetterDate)}</dd>
        </>
      )}
      <dt>Reason</dt>
      <dd className={styles.prose}>{r.reason}</dd>
      {r.summaryText === null ? null : (
        <>
          <dt>Summary</dt>
          <dd className={styles.prose}>{r.summaryText}</dd>
        </>
      )}
      {outgoing ? (
        <>
          <dt>National referral exchange</dt>
          <dd>{r.hie.words}</dd>
        </>
      ) : null}
    </dl>
  );
}

function History({ referral }: { referral: ReferralDetailView }) {
  return (
    <ol className={styles.history} aria-label="History">
      {referral.events.map((e) => (
        <li key={e.id} className={styles.event}>
          <span className={styles.eventTitle}>{REFERRAL_EVENT_KIND_LABELS[e.kind]}</span>
          <span className={styles.meta}>
            {`${formatInstant(e.occurredAt)}${e.channel === null ? '' : `, ${REFERRAL_CHANNEL_LABELS[e.channel].toLowerCase()}`}. Recorded by ${e.recordedBy.displayName}${e.recordedAt === e.occurredAt ? '' : ` at ${formatInstant(e.recordedAt)}`}.`}
          </span>
          {e.note === null ? null : <p className={styles.prose}>{e.note}</p>}
        </li>
      ))}
    </ol>
  );
}

function FeedbackLetters({
  referral,
  mayWrite,
  online,
}: {
  referral: ReferralDetailView;
  mayWrite: boolean;
  online: boolean;
}) {
  const queryClient = useQueryClient();
  const [printing, setPrinting] = useState<ReferralFeedbackLetterView | null>(null);
  const [correcting, setCorrecting] = useState<ReferralFeedbackLetterView | null>(null);
  const correct = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      feedbackEnteredInError(id, reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: referralKeys.all });
      toasts.show({ title: 'Feedback letter marked entered in error' });
      setCorrecting(null);
    },
  });
  if (referral.feedbackLetters.length === 0) {
    return <p className={styles.meta}>No feedback letter yet.</p>;
  }
  return (
    <>
      <ul className={styles.letters} aria-label="Feedback letters">
        {referral.feedbackLetters.map((l) => (
          <li key={l.id} className={styles.letter}>
            <span>
              {'Feedback letter '}
              <span className="wodi-id">{l.feedbackNumber}</span>
              <span className={styles.meta}>
                {` Issued ${formatInstant(l.issuedAt)} by ${l.author.displayName}${l.printCount === 0 ? ', not printed yet' : `, printed ${String(l.printCount)} ${l.printCount === 1 ? 'time' : 'times'}`}`}
              </span>
              {l.enteredInError === null ? null : (
                <span className={styles.meta}>
                  {` Entered in error: ${l.enteredInError.reason}`}
                </span>
              )}
            </span>
            <span className={styles.actions}>
              {l.status === 'entered_in_error' ? (
                <Tag state="neutral">Entered in error</Tag>
              ) : (
                <>
                  <Button
                    dense
                    isDisabled={!online}
                    onPress={() => {
                      setPrinting(l);
                    }}
                  >
                    Print
                  </Button>
                  {mayWrite ? (
                    <Button
                      dense
                      variant="quiet"
                      isDisabled={!online}
                      onPress={() => {
                        setCorrecting(l);
                      }}
                    >
                      Entered in error
                    </Button>
                  ) : null}
                </>
              )}
            </span>
          </li>
        ))}
      </ul>
      {printing === null ? null : (
        <Dialog
          title="Print feedback letter"
          isOpen
          onOpenChange={(open) => {
            if (!open) setPrinting(null);
          }}
        >
          <FeedbackPrinter id={printing.id} referralId={referral.id} />
          <DialogActions>
            <Button
              onPress={() => {
                setPrinting(null);
              }}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {correcting === null ? null : (
        <ReasonDialog
          title="Mark feedback letter entered in error"
          isOpen
          onOpenChange={(open) => {
            if (!open) setCorrecting(null);
          }}
          confirmLabel="Mark entered in error"
          pendingLabel="Saving…"
          isPending={correct.isPending}
          error={correct.isError ? errorText(correct.error, "It couldn't be saved.") : null}
          onConfirm={(reason) => {
            correct.mutate({ id: correcting.id, reason });
          }}
        >
          <p className={styles.meta}>
            The letter stays in the record, marked entered in error, and is not printed again. Write
            a new one if the referring facility needs it.
          </p>
        </ReasonDialog>
      )}
    </>
  );
}

function ReferralView({ referral }: { referral: ReferralDetailView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const queryClient = useQueryClient();
  const [updating, setUpdating] = useState(false);
  const [printingLetter, setPrintingLetter] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const has = (p: Parameters<typeof can>[1]) => session !== null && can(session, p);
  const outgoing = referral.direction === 'outgoing';
  const standing = referral.status !== 'entered_in_error';
  const mayUpdate = has('referrals.track') && referral.allowedUpdates.length > 0;
  const correct = useMutation({
    mutationFn: (reason: string) => referralEnteredInError(referral.id, reason),
    onSuccess: async (next) => {
      queryClient.setQueryData(referralKeys.detail(referral.id), next);
      await queryClient.invalidateQueries({ queryKey: referralKeys.all });
      toasts.show({ title: 'Referral marked entered in error' });
      setCorrecting(false);
    },
  });
  useShortcut(
    'u',
    outsideDialogs(() => {
      if (mayUpdate && online) setUpdating(true);
    }),
    { description: 'Record update', enabled: mayUpdate },
  );
  const tag = referralStatusTag(referral.status);
  return (
    <>
      <PageHeader
        title={outgoing ? 'Referral sent' : 'Referral received'}
        description={`${outgoing ? 'To' : 'From'} ${referral.otherFacility.name}`}
        actions={
          <>
            {mayUpdate ? (
              <Button
                variant="primary"
                isDisabled={!online}
                aria-keyshortcuts="U"
                onPress={() => {
                  setUpdating(true);
                }}
              >
                Record update
              </Button>
            ) : null}
            {outgoing && referral.letter !== null ? (
              <Button
                isDisabled={!online}
                onPress={() => {
                  setPrintingLetter(true);
                }}
              >
                Print letter
              </Button>
            ) : null}
            {!outgoing && standing && has('referrals.write') ? (
              <WriteFeedbackLetter referralId={referral.id} isDisabled={!online} />
            ) : null}
            <Link to="/referrals" search={{ direction: referral.direction }}>
              All referrals
            </Link>
          </>
        }
      />
      {!online ? (
        <Notice tone="info">
          No connection to Wodi. This shows what was last loaded; updates and prints wait for the
          connection.
        </Notice>
      ) : null}
      {referral.enteredInError === null ? null : (
        <Notice tone="info">
          {`Entered in error ${formatInstant(referral.enteredInError.at)} by ${referral.enteredInError.by.displayName}: ${referral.enteredInError.reason}. Do not act on it.`}
        </Notice>
      )}
      {mayUpdate ? <ShortcutHint combo="u" action="record an update" /> : null}
      <Section title="Referral" actions={<Tag state={tag.state}>{tag.word}</Tag>}>
        <Facts referral={referral} />
      </Section>
      {outgoing ? null : (
        <Section
          title="Feedback letters"
          description="Letters back to the referring facility, newest first."
          {...(standing && has('referrals.incoming.record')
            ? {
                actions: (
                  <Button
                    dense
                    variant="quiet"
                    isDisabled={!online}
                    onPress={() => {
                      setCorrecting(true);
                    }}
                  >
                    Mark referral entered in error
                  </Button>
                ),
              }
            : {})}
        >
          <FeedbackLetters referral={referral} mayWrite={has('referrals.write')} online={online} />
        </Section>
      )}
      <Section title="History" description="Oldest first. Nothing is changed or deleted.">
        <History referral={referral} />
      </Section>
      {updating ? (
        <RecordUpdateDialog
          referral={referral}
          onClose={() => {
            setUpdating(false);
          }}
        />
      ) : null}
      {printingLetter && referral.letter !== null ? (
        <Dialog
          title="Print referral letter"
          isOpen
          onOpenChange={(open) => {
            if (!open) setPrintingLetter(false);
          }}
        >
          <ReferralPrinter id={referral.letter.id} listKeys={[referralKeys.detail(referral.id)]} />
          <DialogActions>
            <Button
              onPress={() => {
                setPrintingLetter(false);
              }}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
      {correcting ? (
        <ReasonDialog
          title="Mark referral entered in error"
          isOpen
          onOpenChange={setCorrecting}
          confirmLabel="Mark entered in error"
          pendingLabel="Saving…"
          isPending={correct.isPending}
          error={correct.isError ? errorText(correct.error, "It couldn't be saved.") : null}
          onConfirm={(reason) => {
            correct.mutate(reason);
          }}
        >
          <p className={styles.meta}>
            For a referral logged on the wrong patient or visit. It stays in the record, marked
            entered in error, with your reason.
          </p>
        </ReasonDialog>
      ) : null}
    </>
  );
}

export function ReferralPage() {
  const { referralId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const referral = useQuery({
    ...referralQuery(referralId),
    enabled: session !== null && can(session, 'referrals.read'),
  });
  if (session === null || !can(session, 'referrals.read')) {
    return (
      <>
        <PageHeader title="Referral" />
        <Notice tone="info">
          You don't have permission to see referrals. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }
  if (referral.isPending) return <LoadingBlock label="Loading the referral" rows={6} />;
  if (referral.isError) {
    return (
      <>
        <PageHeader title="Referral" />
        <Notice
          tone="error"
          action={<Button onPress={() => void referral.refetch()}>Try again</Button>}
        >
          {errorText(referral.error, "The referral couldn't be loaded. Try again.")}
        </Notice>
      </>
    );
  }
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={referral.data.patient.id} />
      <ReferralView referral={referral.data} />
    </div>
  );
}
